import {
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  ErrorState,
  Skeleton,
  TOUCH,
} from "@nymbl/nos2-rules";
import { useId, useState } from "react";
import { RulesTable } from "@/components/calendar/RulesTable";
import { useCalendarStatus, useConnectCalendar, useDisconnectCalendar } from "@/hooks/use-calendar";
import { ApiError } from "@/lib/time/api-client";

const RETURN_TO = "/dashboard/profile?tab=calendar";
const FORBIDDEN = 403;

interface CalendarSectionProps {
  /** `?rule=new`: the Add rule dialog is open. */
  adding: boolean;
  onAddingChange: (open: boolean) => void;
}

/**
 * Profile → Calendar: whether Outlook is connected (Connect / Disconnect) and the person's match
 * rules. Nothing while impersonating (the status read answers 403) or when Microsoft sign-in is
 * not set up.
 */
export function CalendarSection({ adding, onAddingChange }: CalendarSectionProps) {
  const headingId = useId();
  const status = useCalendarStatus();
  const disconnect = useDisconnectCalendar();
  const [confirming, setConfirming] = useState(false);
  const connect = useConnectCalendar(() => RETURN_TO);

  if (status.isError && status.error instanceof ApiError && status.error.status === FORBIDDEN) {
    return null;
  }
  if (status.isSuccess && !status.data.available) return null;

  return (
    <section aria-labelledby={headingId}>
      <Card className="border-border/50">
        <CardContent className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 id={headingId} className="font-display text-xl font-bold">
                Calendar
              </h2>
              {status.isSuccess && (
                <p className="text-sm text-muted-foreground">
                  {status.data.connected ? "Connected" : "Not connected"}
                </p>
              )}
            </div>
            {status.isSuccess &&
              (status.data.connected ? (
                <Button
                  type="button"
                  variant="outline"
                  className={TOUCH}
                  disabled={disconnect.isPending}
                  onClick={() => setConfirming(true)}
                >
                  Disconnect calendar
                </Button>
              ) : (
                <Button
                  type="button"
                  className={TOUCH}
                  disabled={connect.pending}
                  onClick={connect.connect}
                >
                  Connect calendar
                </Button>
              ))}
          </div>

          {status.isPending && <Skeleton className="h-11 w-full" />}
          {status.isError && (
            <ErrorState message={status.error.message} onRetry={() => void status.refetch()} />
          )}
          {status.isSuccess && <RulesTable adding={adding} onAddingChange={onAddingChange} />}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Disconnect your calendar?"
        description="Suggestions stop; your rules stay."
        hideDescription
        confirmLabel="Disconnect"
        variant="destructive"
        onConfirm={async () => {
          try {
            await disconnect.mutateAsync();
            setConfirming(false);
          } catch {
            // The hook toasted the server's text; the confirm stays open to retry or cancel.
          }
        }}
      />
    </section>
  );
}
